import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Circle, PlayCircle, Award, FileText, BookOpen, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  not_started: { label: "Not started", tone: "bg-[#FAF4F5] text-[#6B5E62]", Icon: Circle },
  in_progress: { label: "In progress", tone: "bg-[#FCF3E8] text-[#8B5A1D]", Icon: PlayCircle },
  completed:   { label: "Completed",   tone: "bg-[#F0F7EE] text-[#3C7A2B]", Icon: CheckCircle2 },
};

export default function Dashboard() {
  const { user, loading, logout, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login?next=/dashboard"); return; }
    axios.get(`${API}/user/dashboard`, { headers: authHeaders() })
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Could not load dashboard"));
  }, [user, loading, navigate, authHeaders]);

  if (loading || !user) return <div className="py-24 text-center text-[#6B5E62]">Loading…</div>;
  if (err) return <div className="max-w-2xl mx-auto py-16 px-6 text-center text-[#8C4A57]">{err}</div>;
  if (!data) return <div className="py-24 text-center text-[#6B5E62]">Preparing your dashboard…</div>;

  const items = data.items || [];
  const courses = items.filter(i => i.type === "course");
  const ebooks  = items.filter(i => i.type === "ebook");

  return (
    <div className="bg-white py-14" data-testid="student-dashboard">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-start justify-between mb-8 gap-4">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#8C4A57] mb-2">Your library</div>
            <h1 className="font-serif text-3xl sm:text-4xl text-[#2D2628]">Welcome back{data.user.name ? `, ${data.user.name.split(" ")[0]}` : ""}</h1>
            <p className="text-sm text-[#6B5E62] mt-1">{data.user.email}</p>
          </div>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="dashboard-logout"
            className="text-[11px] tracking-widest uppercase text-[#8C4A57] border border-[#8C4A57] rounded-sm px-3 py-2 hover:bg-[#8C4A57] hover:text-white transition inline-flex items-center gap-1">
            <LogOut className="w-3.5 h-3.5"/> Sign out
          </button>
        </div>

        <div className="mb-8">
          <Link to="/redeem" data-testid="dashboard-redeem-link"
            className="inline-flex items-center gap-2 text-[11px] tracking-widest uppercase text-[#8C4A57] hover:underline">
            → Have an access code? Redeem it here
          </Link>
        </div>

        {items.length === 0 && (
          <div className="border border-dashed border-[#E8DCDD] rounded-sm p-12 text-center bg-[#FAF4F5]">
            <p className="text-[#4A3E42] mb-4">You do not have any active purchases attached to this account yet.</p>
            <p className="text-xs text-[#6B5E62] mb-6">Registered with a different email than your last checkout? Sign out and re-register with that email — your purchases will attach automatically.</p>
            <Link to="/courses" className="inline-block bg-[#8C4A57] text-white text-[12px] tracking-[0.14em] uppercase font-semibold px-6 py-3 rounded-sm hover:bg-[#6f3945] transition">Browse courses</Link>
          </div>
        )}

        {courses.length > 0 && (
          <section className="mb-12">
            <h2 className="font-serif text-2xl mb-5">Courses ({courses.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {courses.map(it => <Card key={it.product_id} it={it} />)}
            </div>
          </section>
        )}

        {ebooks.length > 0 && (
          <section>
            <h2 className="font-serif text-2xl mb-5">Ebooks ({ebooks.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {ebooks.map(it => <Card key={it.product_id} it={it} />)}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function Card({ it }) {
  const s = STATUS[it.status] || STATUS.not_started;
  const isCourse = it.type === "course";
  const openHref = it.session_id ? `/payment/success?session_id=${encodeURIComponent(it.session_id)}` : "/courses";
  return (
    <div data-testid={`dash-item-${it.product_id}`} className="border border-[#E8DCDD] rounded-sm bg-white overflow-hidden flex">
      <div className="w-24 h-32 flex-shrink-0 bg-[#FAF4F5] overflow-hidden">
        {it.cover && <img src={it.cover.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${it.cover}` : it.cover} alt="" className="w-full h-full object-cover"/>}
      </div>
      <div className="flex-1 p-4 flex flex-col min-w-0">
        <div className="text-[9px] tracking-widest uppercase text-[#8C4A57] mb-1">{it.category_label}{it.via_bundle && " · Via bundle"}</div>
        <div className="font-serif text-sm text-[#2D2628] leading-tight mb-2 line-clamp-2">{it.title}</div>
        <div className={`inline-flex items-center gap-1 text-[10px] tracking-widest uppercase px-2 py-1 rounded-sm w-fit ${s.tone}`}>
          <s.Icon className="w-3 h-3"/> {s.label}
        </div>
        {isCourse && (
          <div className="mt-2 space-y-1 text-[11px] text-[#6B5E62]">
            {it.modules_total > 0 && (
              <div data-testid={`dash-modules-${it.product_id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[#4A3E42] font-medium">
                    {it.quiz_passed
                      ? `All ${it.modules_total} modules complete`
                      : it.modules_completed > 0
                        ? `You're ${it.modules_completed} of ${it.modules_total} modules in`
                        : `${it.modules_total} modules — start when you're ready`}
                  </span>
                  <span className="text-[10px] tracking-widest uppercase text-[#8C4A57]">
                    {Math.round((it.modules_completed / it.modules_total) * 100)}%
                  </span>
                </div>
                <div className="h-1.5 w-full bg-[#F1E4E6] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#8C4A57] transition-all duration-500"
                    style={{ width: `${Math.min(100, (it.modules_completed / it.modules_total) * 100)}%` }}
                  />
                </div>
              </div>
            )}
            {it.downloads_accessed_count > 0 && <div className="flex items-center gap-1"><FileText className="w-3 h-3"/>{it.downloads_accessed_count} download{it.downloads_accessed_count > 1 ? "s" : ""} opened</div>}
            {it.quiz_score != null && <div>Quiz: {Math.round(it.quiz_score)}%{it.quiz_passed ? " · Passed" : ""}</div>}
            {it.certificate_id && <div className="text-[#3C7A2B] flex items-center gap-1"><Award className="w-3 h-3"/>Certificate #{it.certificate_id}</div>}
          </div>
        )}
        {!isCourse && it.downloads_accessed_count > 0 && (
          <div className="mt-2 text-[11px] text-[#6B5E62] flex items-center gap-1"><BookOpen className="w-3 h-3"/>{it.downloads_accessed_count} download{it.downloads_accessed_count > 1 ? "s" : ""} opened</div>
        )}
        <Link to={openHref} data-testid={`dash-open-${it.product_id}`}
          className="mt-auto pt-3 text-[11px] tracking-widest uppercase text-[#8C4A57] font-semibold hover:underline">
          Open →
        </Link>
      </div>
    </div>
  );
}
